// Nebari navigation chrome — theme tokens.
//
// Single source of truth for the colors the section bar + rail consume. Light
// and dark variants. Pulled directly from the Claude Design brief
// (`nav-brief/BRIEF.md` §7) — the existing Tailwind palette (moss/bark/stone/
// clay) covers most of these, but the chrome needs a tighter, semantically-
// named subset that doesn't drift if the broader palette evolves.

export type NavTheme = 'light' | 'dark';

export interface NavThemeTokens {
  /** Section bar + rail background. */
  bg: string;
  /** Surface for active/hover backgrounds in the section bar. */
  surface: string;
  /** Active rail item pill — brand cream. Same in light + dark. */
  cream: string;
  /** Hairline border below section bar / right of rail. */
  border: string;
  /** Primary text. */
  ink: string;
  /** Section-bar inactive label. */
  muted: string;
  /** Rail idle icon. */
  subtle: string;
  /** Disabled / stubbed section. */
  faint: string;
  // Brand accents — the brand block, the section underline and the avatar.
  barkDark: string;
  moss: string;
  moss2: string;
  /**
   * The accent as it paints directly on the chrome background — the active
   * bottom tab, the raised Search circle. Distinct from `barkDark`, which is
   * only ever ink on the cream pill and so stays dark in both themes: reused
   * here it left the active tab at 1.1:1 against the dark bar.
   */
  accent: string;
  /** Warn badge (e.g. critical-SKU count). */
  warn: string;
  /** Page background (everything outside the chrome). */
  pageBg: string;
}

export const NAV_THEMES: Record<NavTheme, NavThemeTokens> = {
  light: {
    bg: '#ffffff',
    surface: '#f5f6f4',
    cream: '#EAF3DE',
    border: '#dce0dc',
    ink: '#1e1a14',
    muted: '#5d675d',
    subtle: '#748074',
    faint: '#a4ada4',
    barkDark: '#173404',
    moss: '#0e874b',
    moss2: '#0c6b3d',
    accent: '#173404',
    warn: '#cc6726',
    // Light moss tint per the Settings → Users design — a subtle green-cast
    // page background that lets white cards pop.
    pageBg: '#eef6ed',
  },
  dark: {
    bg: '#181d18',
    surface: '#212620',
    // Brand cream stays cream in dark — intentional brand accent that reads
    // well even against #181d18. See BRIEF.md §8.3.
    cream: '#EAF3DE',
    border: 'rgba(255, 255, 255, 0.08)',
    ink: '#e8ede5',
    muted: '#a4ada4',
    subtle: '#8c948c',
    faint: '#5a615a',
    barkDark: '#173404',
    moss: '#18a85e',
    moss2: '#0e874b',
    accent: '#45c47d',
    warn: '#e0824a',
    pageBg: '#0a0d0a',
  },
};
